import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Label,
  Button,
  Alert,
} from "reactstrap";
import Loader from "./Loader";
import { useSelector, useDispatch } from "react-redux";
import { companyUpdateStatusState } from "../../store/actions";
import { COMPANY_DELETE_STATUS } from "../../store/company/actionType";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";

const DeleteClientModal = ({
  show,
  onDeleteClick,
  onCloseClick,
  customerToDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Handle both user and tenant/company deletion
  const isUser = customerToDelete?.firstName || customerToDelete?.email;
  const displayName = isUser
    ? `${customerToDelete.firstName || ""} ${
        customerToDelete.lastName || ""
      }`.trim()
    : customerToDelete?.tenantName || customerToDelete?.name;
  const displayId = isUser
    ? customerToDelete?.email
    : customerToDelete?.portalId;

  const { tenantName, portalId } = customerToDelete;

  const [textInput, setTextInput] = useState("");

  const { companyDeleteStatus, companyDeleteError } = useSelector((state) => ({
    companyDeleteStatus: state.Company.companyDeleteStatus,
    companyDeleteError: state.Company.companyDeleteError,
  }));

  const handleTextInputChange = (e) => {
    setTextInput(e.target.value);
  };

  const handleConfirmDelete = () => {
    onDeleteClick();
  };

  const handleCloseModal = () => {
    setTextInput("");
    onCloseClick();
  };

  const handleDeleteSuccess = () => {
    setTimeout(() => {
      dispatch(companyUpdateStatusState(COMPANY_DELETE_STATUS, "idle"));
      handleCloseModal();
    }, 1500);
  };

  return (
    <Modal
      isOpen={show}
      toggle={handleCloseModal}
      centered={true}
      className="delete-client-modal"
    >
      <ModalHeader toggle={handleCloseModal}>
        <div className="mt-2 text-center">
          <h4>
            {isUser
              ? t("users.shared.deleteModal.titleUser")
              : t("users.shared.deleteModal.titlePortal")}
          </h4>
        </div>
      </ModalHeader>
      <ModalBody className="py-3 px-5">
        {companyDeleteStatus === "failed" && (
          <Alert color="danger" className="text-center fw-semibold">
            {companyDeleteError
              ? translateApiError(companyDeleteError)
              : companyDeleteError}
          </Alert>
        )}
        <div className="d-flex flex-column">
          <p>
            {t("users.shared.deleteModal.messagePrefix")}
            <strong>{displayName}</strong>
            {displayId && (
              <>
                {" "}
                (<strong>{displayId}</strong>)
              </>
            )}
            {t("users.shared.deleteModal.messageSuffix")}
          </p>
          <ul>
            <li>
              <strong>{displayName}</strong>
              {displayId && (
                <>
                  {" "}
                  (<strong>{displayId}</strong>)
                </>
              )}
            </li>
          </ul>
          {!isUser && (
            <>
              <p className="mt-3">
                {t("users.shared.deleteModal.typeToConfirm")}
              </p>
              <Label htmlFor="deleteInput">
                <Input
                  type="text"
                  placeholder={t(
                    "users.shared.deleteModal.tenantNamePlaceholder"
                  )}
                  onChange={handleTextInputChange}
                  value={textInput}
                />
              </Label>
            </>
          )}
          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button
              type="button"
              color={companyDeleteStatus === "success" ? "success" : "danger"}
              disabled={
                companyDeleteStatus === "loading" ||
                companyDeleteStatus === "success" ||
                (!isUser && textInput !== displayName)
              }
              onClick={handleConfirmDelete}
            >
              {companyDeleteStatus === "loading" ? (
                <Loader size="sm"></Loader>
              ) : companyDeleteStatus === "success" ? (
                <>
                  {t("users.shared.deleteModal.successful")}
                  {handleDeleteSuccess()}
                </>
              ) : (
                t("common.actions.delete")
              )}
            </Button>
            <Button
              type="button"
              className="text-primary"
              color="light"
              onClick={handleCloseModal}
            >
              {t("common.actions.cancel")}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

DeleteClientModal.propTypes = {
  onCloseClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  show: PropTypes.any,
  companyToDelete: PropTypes.object,
};

export default DeleteClientModal;
